import { useState, useEffect, useRef, useMemo } from "react";
import moment from "moment";

// components
import ReactApexChart from "react-apexcharts";
import * as XLSX from 'xlsx';
import { convertCurrency, covertTimeZone, numberFormat, NewCovertTimeZone } from "../../helper";

// apollo-client
import {
  REPORT_OPTIONS,
} from "../../apollo/exchangeRate/Query";
import { useLazyQuery } from "@apollo/client";
import "./index.css";

// react-icons
import LocalActivityIcon from "@mui/icons-material/LocalActivity";

// react bootrap
import { Table, Card, Spinner, Button, Form, Col, Row } from "react-bootstrap";
import { AiOutlineFileText, AiOutlinePieChart } from "react-icons/ai";
import { RiMoneyDollarCircleLine } from "react-icons/ri";
import { MdOutlineDateRange, MdOutlineReport } from "react-icons/md";
import { ConvertDay, startDateAndEndDate } from "../../components/handleDate";
import AnimationLoading from "../../components/Animationloading";
import EmptyComponent from "../../components/EmptyComponent";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ReactToPrint from "react-to-print";
import CurrentDateComponents from "../../components/CurrentDateComponents";
import _ from "lodash";
import { GET_BRANDS } from "../../apollo/brand/Query";
import { GET_CATEGORYS } from "../../apollo/category/query";

function ReportOptions() {
  const currentBrand = localStorage.getItem("CURRENT_BRAND");
  const [reportSumAllOrder, setReportSumAllOrder] = useState();
  const [reportBestSale, setReportBestSale] = useState([]);
  const [reportOptionsData, setReportOptionsData] = useState();
  let shopId = JSON.parse(localStorage.getItem("SHOP_DATA"))?.id;
  const printRef = useRef();
  const [isCurrentModals, setIsCurrentModals] = useState(false);
  const [searchBrand, setSearchBrand] = useState(currentBrand);
  const [categories, setCategories] = useState([]);
  const [searchCategory, setSearchCategory] = useState("ALL");
  const [status, setStatus] = useState("COMPLETED");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [getBrand, { data: brandData }] = useLazyQuery(GET_BRANDS, { fetchPolicy: "cache-first" });
  const [getCategory, { data: categoryData }] = useLazyQuery(GET_CATEGORYS, { fetchPolicy: "cache-first" });

  useEffect(() => {
    if (categoryData?.categories?.data?.length > 0) {
      const result = categoryData?.categories?.data;
      setCategories(result);
    }
  }, [categoryData]);

  useEffect(() => {
    if (searchCategory === "ALL") return;
    const fetchBrands = async () => {
      try {
        let _where = {
          shop: shopId,
          isDeleted: false
        };
        if (searchCategory !== "ALL") {
          _where = { ..._where, category: searchCategory };
        }
        // TODO: Get all brand
        await getBrand({
          variables: {
            where: _where,
          },
        });
      } catch (error) {
        console.log("Error in fetchBrands:", error);
      }
    };
    fetchBrands();
  }, [searchCategory]);

  const brands = useMemo(() => {
    const result = brandData?.brands?.data
    return result;
  }, [brandData]);

  // Use only the new unified report options query
  const [LoadReportOptions, { data: getReportOptionsData, loading: reportOptionsLoading, error: reportOptionsError }] =
    useLazyQuery(REPORT_OPTIONS, { fetchPolicy: "cache-and-network" });

  const [activeButton, setActiveButton] = useState("thisMonth");
  // states for get by adding date-time

  const [filter, setFilter] = useState({
    createdAt_gte: startDateAndEndDate().startMonth,
    createdAt_lt: startDateAndEndDate().endMonth,
  });
  // ============== > useEffect <==============

  useEffect(() => {
    if(categories?.length <= 0) {
      getCategory({
        variables: {
          limit: 100,
          where: {
            shop: shopId,
            isDeleted: false,
          },
        },
      });
    }
  }, []);

  // Handle new reportOptions data
  useEffect(() => {
    if (getReportOptionsData?.reportOptions) {
      const { data, doc } = getReportOptionsData.reportOptions;
      setReportOptionsData(getReportOptionsData.reportOptions);
      
      // Update total items for pagination
      if (data.billAmount !== undefined) {
        setTotalItems(data.billAmount);
      }
      
      // Update existing states with new data structure
      if (data) {
        setReportSumAllOrder({
          billAmount: data.billAmount,
          productAmount: data.productAmount,
          totalPrice: data.totalPrice,
          sumPriceBaht: data.sumPriceBaht,
          sumPriceUsd: data.sumPriceUsd,
          shipCost: data.shipCost || 0 // Add shipping cost to total report
        });
      }
      
      // Process doc array for best selling products
      if (doc && doc.length > 0) {
        const processedBestSale = doc.map(item => ({
          productName: item.stockName,
          amount: item.amount,
          category: item.categoryName,
          brand: item.brandName,
          unit: '', // Not provided in new API
          totalPrice: 0, // Calculate if needed
          price: item.options?.reduce((sum, opt) => sum + (opt.price || 0), 0) || 0,
          currency: item.currency || 'LAK', // Default currency
          shippingCost: item.shippingCost || 0, // Add shipping cost to stock item
          status: item.status || 'unknown' // Add status field
        }));
        setReportBestSale(processedBestSale);
      } else {
        setReportBestSale([]);
      }
    }
  }, [getReportOptionsData]);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter?.createdAt_gte, filter?.createdAt_lt, searchCategory, searchBrand, status]);

  useEffect(() => {
    fetchDataReports();
  }, [filter?.createdAt_gte, filter?.createdAt_lt, shopId, searchCategory, searchBrand, status, currentPage, itemsPerPage, LoadReportOptions]);

  const fetchDataReports = async () => {
    try {
      if (reportOptionsLoading) return;
      let _where = {};
      if (filter?.createdAt_gte && filter?.createdAt_lt) {
        _where = {
          ..._where,
          createdAt_gte: NewCovertTimeZone(filter?.createdAt_gte),
          createdAt_lt: NewCovertTimeZone(filter?.createdAt_lt)
        };
      }

      if (searchCategory !== "ALL") {
        const category = categories?.find((e) => e?.id === searchCategory);
        _where = {
          ..._where,
          category: category?.id,
          categoryName: category?.name,
        };
      }

      if (searchBrand !== "ALL") {
        const brand = brands?.find((e) => e?.id === searchBrand);
        _where = {
          ..._where,
          brand: brand?.id,
          brandName: brand?.name,
        };
      }

      if (status !== "ALL") {
        _where = {
          ..._where,
          status: status,
        };
      }

      // Use the new unified reportOptions API with pagination
      const skip = (currentPage - 1) * itemsPerPage;
      await LoadReportOptions({
        variables: {
          where: _where,
          skip: skip,
          limit: itemsPerPage,
        },
      });
    } catch (error) {
      console.log("Error: ", error);
    }
  };


  // ============== > fuctions <==============
  let state = {
    series: [
      {
        data: Array.isArray(reportBestSale) && reportBestSale.length > 0 
          ? reportBestSale.map((e) => e?.amount || 0)
          : [0],
      },
    ],
    options: {
      chart: {
        type: "bar",
        height: 350,
      },
      xaxis: {
        categories: Array.isArray(reportBestSale) && reportBestSale.length > 0 
          ? reportBestSale.map((e) => e?.productName || 'ບໍ່ມີຊື່')
          : ['ບໍ່ມີຂໍ້ມູນ'],
      },
    },
  };

  // Generate Excel file
  const generateExcel = () => {
    try {
      // Validate data before proceeding
      if (!reportBestSale && !reportSumAllOrder) {
        alert('ບໍ່ມີຂໍ້ມູນສຳລັບການສ້າງ Excel. ກະລຸນາໂຫຼດຂໍ້ມູນກ່ອນ.');
        return;
      }

      // Show loading indicator
      const loadingDiv = document.createElement('div');
      loadingDiv.style.position = 'fixed';
      loadingDiv.style.top = '0';
      loadingDiv.style.left = '0';
      loadingDiv.style.width = '100%';
      loadingDiv.style.height = '100%';
      loadingDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
      loadingDiv.style.display = 'flex';
      loadingDiv.style.justifyContent = 'center';
      loadingDiv.style.alignItems = 'center';
      loadingDiv.style.zIndex = '9999';
      loadingDiv.innerHTML = `
        <div style="text-align: center;">
          <div class="spinner-border text-primary" role="status"></div>
          <p style="margin-top: 10px; font-family: 'Phetsarath', sans-serif;">ກຳລັງສ້າງ Excel...</p>
        </div>
      `;
      document.body.appendChild(loadingDiv);

      // Get shop data with validation
      let shopData;
      try {
        shopData = JSON.parse(localStorage.getItem("SHOP_DATA") || '{}');
      } catch (e) {
        shopData = {};
      }
      const shopName = shopData?.name || '4B POS Shop';
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      
      // Format the date range with validation
      let dateRange;
      try {
        const startDate = filter?.createdAt_gte ? covertTimeZone(filter.createdAt_gte) : 'N/A';
        const endDate = filter?.createdAt_lt ? covertTimeZone(filter.createdAt_lt) : 'N/A';
        dateRange = `${startDate} - ${endDate}`;
      } catch (e) {
        dateRange = 'ບໍ່ມີຂໍ້ມູນວັນທີ';
      }
      
      // Create worksheet for best selling products
      // Always create the worksheet, even if data is empty
      const headers = [
        'ລຳດັບ', // ລຳດັບ
        'ຊື່ສິນຄ້າ', // ຊື່ສິນຄ້າ
        'ປະເພດ', // ປະເພດ
        'ແບຣນ', // ແບຣນ
        'ຫນ່ວຍ', // ຫນ່ວຍ
        'ຈຳນວນ', // ຈຳນວນ
        'ລາຄາ', // ລາຄາ
        'ຄ່າຂົນສົ່ງ', // ຄ່າຂົນສົ່ງ
        'ສະຖານະ', // ສະຖານະ
      ];
      
      // Prepare data rows with validation
      let data = [];
      if (reportBestSale && Array.isArray(reportBestSale) && reportBestSale.length > 0) {
        data = reportBestSale.map((item, index) => {
          // Validate each item to prevent errors
          const safeItem = item || {};
          return [
            index + 1,
            safeItem.productName || 'ບໍ່ມີຊື່',
            safeItem.category || 'ບໍ່ມີປະເພດ',
            safeItem.brand || 'ບໍ່ມີແບຣນ',
            safeItem.unit || 'ບໍ່ມີຫນ່ວຍ',
            safeItem.amount || 0,
            `${numberFormat(safeItem.price ?? 0)} ${convertCurrency(safeItem.currency || 'LAK')}`,
            `${numberFormat(safeItem.shippingCost ?? 0)} ₭`,
            safeItem.status || 'unknown',
          ];
        });
      } else {
         // Add a row indicating no data
         data = [['', 'ບໍ່ມີຂໍ້ມູນສິນຄ້າ', '', '', '', '', '', '', '']];
       }
         
       // Add title rows
       const titleRows = [
         [shopName, '', '', '', '', '', '', '', ''],
         ['ລາຍງານການຂາຍສິນຄ້າ', '', '', '', '', '', '', '', ''], // ລາຍງານການຂາຍສິນຄ້າ
         [`ສິນຄ້າຂາຍດີ ${dateRange}`, '', '', '', '', '', '', '', ''], // ສິນຄ້າຂາຍດີ
         ['', '', '', '', '', '', '', '', ''], // Empty row for spacing
       ];
       
       // Combine all rows
       const allRows = [...titleRows, headers, ...data];
       
       // Create worksheet
       const ws = XLSX.utils.aoa_to_sheet(allRows);
       
       // Set column widths
       const colWidths = [{ wch: 10 }, { wch: 30 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 }];
       ws['!cols'] = colWidths;
       
       // Style the title rows
       ws['!merges'] = [
         { s: { r: 0, c: 0 }, e: { r: 0, c: 8 } }, // Merge first row (shop name)
         { s: { r: 1, c: 0 }, e: { r: 1, c: 8 } }, // Merge second row (report title)
         { s: { r: 2, c: 0 }, e: { r: 2, c: 8 } }, // Merge third row (date range)
       ];
       
       // Add worksheet to workbook
       XLSX.utils.book_append_sheet(wb, ws, 'ສິນຄ້າຂາຍດີ'); // ສິນຄ້າຂາຍດີ (Best Selling Products)
      
      // Add summary data worksheet if available
      if (reportSumAllOrder) {
        const summaryData = [
          [shopName, '', ''],
          ['ສະຫລຸບການຂາຍ', '', ''], // ສະຫລຸບການຂາຍ (Sales Summary)
          [`${dateRange}`, '', ''],
          ['', '', ''], // Empty row for spacing
          ['ຈຳນວນບິນ', reportSumAllOrder.billAmount ?? 0, ''], // ຈຳນວນບິນ (Bill Count)
          ['ສິນຄ້າທັງຫມົດ', reportSumAllOrder.productAmount ?? 0, ''], // ສິນຄ້າທັງຫມົດ (Total Products)
          ['ຍອດຂາຍທັງຫມົດ (LAK)', numberFormat(reportSumAllOrder.totalPrice ?? 0), ''], // ຍອດຂາຍທັງຫມົດ (Total Sales LAK)
          ['ຍອດຂາຍທັງຫມົດ (THB)', numberFormat(reportSumAllOrder.sumPriceBaht ?? 0), ''], // ຍອດຂາຍທັງຫມົດ (Total Sales THB)
          ['ຍອດຂາຍທັງຫມົດ (USD)', numberFormat(reportSumAllOrder.sumPriceUsd ?? 0), ''], // ຍອດຂາຍທັງຫມົດ (Total Sales USD)
          ['ຄ່າຂົນສົ່ງທັງຫມົດ (LAK)', numberFormat(reportSumAllOrder.shipCost ?? 0), ''], // ຄ່າຂົນສົ່ງທັງຫມົດ (Total Shipping Cost LAK)
        ];
        
        // Create worksheet
        const wsSummary = XLSX.utils.aoa_to_sheet(summaryData);
        
        // Set column widths
        wsSummary['!cols'] = [{ wch: 30 }, { wch: 20 }, { wch: 10 }];
        
        // Style the title rows
        wsSummary['!merges'] = [
          { s: { r: 0, c: 0 }, e: { r: 0, c: 2 } }, // Merge first row (shop name)
          { s: { r: 1, c: 0 }, e: { r: 1, c: 2 } }, // Merge second row (summary title)
          { s: { r: 2, c: 0 }, e: { r: 2, c: 2 } }, // Merge third row (date range)
        ];
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, wsSummary, 'ສະຫລຸບການຂາຍ'); // ສະຫລຸບການຂາຍ (Sales Summary)
      }
      
      // Generate Excel file
      XLSX.writeFile(wb, `4B_POS_Report_${moment().format('YYYY-MM-DD_HH-mm')}.xlsx`);
      
      // Remove loading indicator
      document.body.removeChild(loadingDiv);
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert('ເກີດຂໍ້ຜິດພາດໃນການສ້າງ Excel. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.');
      
      // Make sure to remove loading indicator in case of error
      const loadingDiv = document.querySelector('div[style*="position: fixed"][style*="z-index: 9999"]');
      if (loadingDiv) {
        document.body.removeChild(loadingDiv);
      }
    }
  };
  
  // download to pdf file
  const generatePdf = () => {
    try {
      console.log('Starting PDF generation...');
      
      // Validate data before proceeding
      if (!reportBestSale && !reportSumAllOrder) {
        alert('ບໍ່ມີຂໍ້ມູນສຳລັບການສ້າງ PDF. ກະລຸນາໂຫຼດຂໍ້ມູນກ່ອນ.');
        return;
      }
      
      // Show loading indicator
      const loadingDiv = document.createElement('div');
      loadingDiv.style.position = 'fixed';
      loadingDiv.style.top = '0';
      loadingDiv.style.left = '0';
      loadingDiv.style.width = '100%';
      loadingDiv.style.height = '100%';
      loadingDiv.style.backgroundColor = 'rgba(255, 255, 255, 0.7)';
      loadingDiv.style.display = 'flex';
      loadingDiv.style.justifyContent = 'center';
      loadingDiv.style.alignItems = 'center';
      loadingDiv.style.zIndex = '9999';
      loadingDiv.innerHTML = `
        <div style="text-align: center;">
          <div class="spinner-border text-primary" role="status"></div>
          <p style="margin-top: 10px; font-family: 'Phetsarath', sans-serif;">ກຳລັງສ້າງ PDF...</p>
        </div>
      `;
      document.body.appendChild(loadingDiv);

      // Get shop data with validation
      let shopData;
      try {
        shopData = JSON.parse(localStorage.getItem("SHOP_DATA") || '{}');
      } catch (e) {
        shopData = {};
      }
      const shopName = shopData?.name || '4B POS Shop';
      
      // Create PDF document
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 10; // margin in mm
      const contentWidth = pageWidth - (margin * 2);
      
      // Get the chart and table elements
      const chartElement = document.querySelector('div[style="width: 100%; margin-bottom: 10px;"] .card');
      const tableElement = document.querySelector('div[style="width: 100%; margin-bottom: 20px;"] .card');
      
      if (!chartElement || !tableElement) {
        console.error('Could not find chart or table elements');
        document.body.removeChild(loadingDiv);
        alert('ບໍ່ສາມາດຊອກຫາຂໍ້ມູນສຳລັບການສ້າງ PDF. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.');
        return;
      }
      
      // Create a header canvas to handle Lao text properly
      const createHeaderCanvas = () => {
        const canvas = document.createElement('canvas');
        canvas.width = 800;
        canvas.height = 100;
        const ctx = canvas.getContext('2d');
        
        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Add title
        ctx.font = 'bold 24px Phetsarath, Noto Sans Lao, Arial, sans-serif';
        ctx.fillStyle = '#3C169B';
        ctx.textAlign = 'center';
        ctx.fillText('ລາຍງານການຂາຍສິນຄ້າ', canvas.width / 2, 35);
        
        // Add shop name and date
        ctx.font = '14px Phetsarath, Noto Sans Lao, Arial, sans-serif';
        ctx.fillStyle = '#333333';
        ctx.textAlign = 'left';
        ctx.fillText(shopName, 50, 70);
        
        ctx.textAlign = 'right';
        // Format date range with validation
        let dateRange;
        try {
          const startDate = filter?.createdAt_gte ? moment(filter.createdAt_gte).format('DD/MM/YYYY') : '';
          const endDate = filter?.createdAt_lt ? moment(filter.createdAt_lt).format('DD/MM/YYYY') : '';
          if (startDate && endDate) {
            dateRange = `${startDate} - ${endDate}`;
          } else {
            dateRange = 'ທຸກວັນທີ';
          }
        } catch (error) {
          dateRange = 'ຂໍ້ຜິດພາດໃນການສະແດງວັນທີ';
        }
        ctx.fillText(`ສິນຄ້າຂາຍດີ ${dateRange}`, canvas.width - 50, 70);
        
        // Add a line
        ctx.strokeStyle = '#3C169B';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.moveTo(50, 85);
        ctx.lineTo(canvas.width - 50, 85);
        ctx.stroke();
        
        return canvas;
      };
      
      // Function to add header to a page
      const addHeader = (pageNum) => {
        const headerCanvas = createHeaderCanvas();
        const headerImgData = headerCanvas.toDataURL('image/png');
        pdf.addImage(headerImgData, 'PNG', margin, margin, contentWidth, 25);
      };
      
      // Function to create page number canvas
      const createPageNumberCanvas = (pageNum, totalPages) => {
        const canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 30;
        const ctx = canvas.getContext('2d');
        
        // Fill background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        
        // Add page number text
        ctx.font = '12px Phetsarath, Noto Sans Lao, Arial, sans-serif';
        ctx.fillStyle = '#666666';
        ctx.textAlign = 'center';
        ctx.fillText(`ໜ້າ ${pageNum} ຂອງ ${totalPages}`, canvas.width / 2, 15);
        
        return canvas;
      };
      
      // Function to add page number
      const addPageNumber = (pageNum, totalPages) => {
        const pageNumberCanvas = createPageNumberCanvas(pageNum, totalPages);
        const pageNumberImgData = pageNumberCanvas.toDataURL('image/png');
        // Position the page number at the bottom center
        const pageNumberWidth = 40; // mm
        pdf.addImage(
          pageNumberImgData, 
          'PNG', 
          (pageWidth - pageNumberWidth) / 2, 
          pageHeight - 10, 
          pageNumberWidth, 
          5
        );
      };
      
      // Capture the chart
      html2canvas(chartElement, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      }).then(chartCanvas => {
        // Add header to first page
        addHeader(1);
        
        // Add chart to first page
        const chartImgData = chartCanvas.toDataURL('image/png');
        const chartHeight = (chartCanvas.height * contentWidth) / chartCanvas.width;
        pdf.addImage(chartImgData, 'PNG', margin, margin + 30, contentWidth, chartHeight);
        
        // Capture the table
        return html2canvas(tableElement, {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        });
      }).then(tableCanvas => {
        // Add new page for table
        pdf.addPage();
        
        // Add header to second page
        addHeader(2);
        
        // Add table to second page
        const tableImgData = tableCanvas.toDataURL('image/png');
        const tableHeight = (tableCanvas.height * contentWidth) / tableCanvas.width;
        pdf.addImage(tableImgData, 'PNG', margin, margin + 30, contentWidth, tableHeight);
        
        // Add page numbers
        const totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          addPageNumber(i, totalPages);
        }
        
        // Save PDF
        pdf.save('4B_POS_Report.pdf');
        
        // Remove loading indicator
        document.body.removeChild(loadingDiv);
      }).catch(error => {
        console.error('Error generating PDF:', error);
        document.body.removeChild(loadingDiv);
        alert('ເກີດຂໍ້ຜິດພາດໃນການສ້າງ PDF. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.');
      });
    } catch (error) {
      console.error('Error in generatePdf:', error);
      alert('ເກີດຂໍ້ຜິດພາດໃນການສ້າງ PDF. ກະລຸນາລອງໃໝ່ອີກຄັ້ງ.');
    }
  };

  return (
    <div
      style={{
        width: "100%", padding: "2em", paddingTop: "1em", paddintBottom: "1em"
      }}
    >
      <Card className="dashboard-header" style={{ marginBottom: "10px" }}>
        <Card.Body>
          <h5 className="chart-title d-flex align-items-center">
            <MdOutlineReport size={24} />
            ລາຍງານອອບຊັນການຂາຍ
          </h5>
        </Card.Body>
      </Card>
      <Card className="dashboard-header" style={{ marginBottom: "10px" }}>
        <Card.Body>
          <Row>
            {/* Date Filter */}
            <Col xs={12} md={3}>
              <Form.Group>
                <Form.Label className="filter-label">ວັນທີ</Form.Label>
                <Button
                  onClick={() => setIsCurrentModals(true)}
                  className="w-100 date-picker-btn"
                >
                  <MdOutlineDateRange size={18} />
                  <span className="date-text">
                    {!filter ? "mm/dd/yyyy" : (
                      `${covertTimeZone(filter?.createdAt_gte)} ຫາ ${covertTimeZone(filter?.createdAt_lt)}`
                    )}
                  </span>
                </Button>
              </Form.Group>

              <CurrentDateComponents
                onCloseModal={() => setIsCurrentModals(false)}
                openModal={isCurrentModals}
                setCdFillter={setFilter}
                setActiveButton={setActiveButton}
                activeButton={activeButton}
              />
            </Col>

            {/* Filters Row */}
            <Col xs={12} md={2} className="mb-3">
              <Form.Group>
                <Form.Label className="filter-label">ປະເພດ</Form.Label>
                <Form.Select
                  className="filter-select"
                  onChange={(e) => setSearchCategory(e?.target?.value)}
                  value={searchCategory}
                >
                  <option value="ALL">--ເລືອກ--</option>
                  {categories?.length > 0 && categories?.map((category, _index) => (
                    <option key={_index} value={category?.id}>
                      {category?.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12} md={2} className="mb-3">
              <Form.Group>
                <Form.Label className="filter-label">ແບຣນ</Form.Label>
                <Form.Select
                  className="filter-select"
                  onChange={(e) => setSearchBrand(e?.target?.value)}
                  value={searchBrand}
                >
                  <option value="ALL">--ເລືອກ--</option>
                  {brands?.length > 0 && brands?.map((brand, _index) => (
                    <option key={_index} value={brand?.id}>
                      {brand?.name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            {/* //TODO: Payment Filter */}
            <Col xs={12} md={2} className="mb-3">
              <Form.Group>
                <Form.Label className="filter-label">ສະຖານະ</Form.Label>
                <Form.Select
                  className="filter-select action-select"
                  onChange={(e) => {
                    const action = e.target.value;
                    setStatus(action);
                  }}
                  defaultValue={status}
                >
                  <option value="ALL">--ເລືອກທັງຫມົດ--</option>
                  <option value="COMPLETED">ຊຳລະແລ້ວ</option>
                  <option value="INDEBTED">ຕີດຫນີ້</option>
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col xs={12} md={2} className="mb-3">
              <Form.Group>
                <Form.Label className="filter-label">ການດຳເນີນການ</Form.Label>
                <Form.Select
                  className="filter-select action-select"
                  onChange={(e) => {
                    const action = e.target.value;
                    if (action === "pdf") {
                      generatePdf();
                    } else if (action === "excel") {
                      generateExcel();
                    } else if (action === "print_A4") {
                      document.getElementById("print-button").click();
                    }
                    // Reset select after action
                    setTimeout(() => {
                      e.target.value = "";
                    }, 500);
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>--ເລືອກການດຳເນີນການ--</option>
                  <option value="excel">ດາວໂຫລດ Excel</option>
                  <option value="pdf">ດາວໂຫລດ PDF</option>
                  <option value="print_A4">ພິມເປັນ A4</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
          
          {/* Hidden Print Trigger */}
          <div style={{ display: 'none' }}>
            <ReactToPrint
              trigger={() => (
                <button id="print-button">Print</button>
              )}
              content={() => printRef.current}
            />
          </div>
        </Card.Body>
      </Card>

      <div style={{ padding: 0 }} ref={printRef}>
        <Row className="mb-4">
          {/* Bills & Products Summary Card */}
          <Col lg={4} md={6} sm={12} className="mb-3">
            <Card className="stat-card shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="stat-icon bill-icon me-3">
                      <AiOutlineFileText size={24} />
                    </div>
                    <div className="stat-info">
                      <h6 className="stat-title mb-1">ຈຳນວນບິນ</h6>
                      <h3 className="stat-value mb-0">{reportSumAllOrder?.billAmount ?? 0}</h3>
                    </div>
                  </div>
                  <div className="d-flex align-items-center">
                    <div className="stat-icon product-icon me-3">
                      <AiOutlinePieChart size={24} />
                    </div>
                    <div className="stat-info">
                      <h6 className="stat-title mb-1">ສິນຄ້າທັງຫມົດ</h6>
                      <h3 className="stat-value mb-0">{reportSumAllOrder?.productAmount ?? 0}</h3>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Sales Total Card */}
          <Col lg={4} md={12} sm={12} className="mb-3">
            <Card className="stat-card shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-start">
                  <div className="stat-icon money-icon me-3">
                    <RiMoneyDollarCircleLine size={24} />
                  </div>
                  <div className="stat-info">
                    <h6 className="stat-title mb-2">ຍອດຂາຍທັງຫມົດ</h6>
                    <div className="currency-values">
                      <div className="d-flex align-items-center mb-1">
                        <span className="currency-symbol me-2">₭</span>
                        <span className="currency-amount">{numberFormat((reportSumAllOrder?.totalPrice ?? 0) - (reportSumAllOrder?.discount ?? 0))}</span>
                      </div>
                      {
                        reportSumAllOrder?.sumPriceBaht > 0 && 
                        <div className="d-flex align-items-center mb-1">
                          <span className="currency-symbol me-2">฿</span>
                          <span className="currency-amount">{numberFormat(reportSumAllOrder?.sumPriceBaht ?? 0)}</span>
                        </div>
                      }
                      {
                        reportSumAllOrder?.sumPriceUsd > 0 && 
                        <div className="d-flex align-items-center">
                          <span className="currency-symbol me-2">$</span>
                          <span className="currency-amount">{numberFormat(reportSumAllOrder?.sumPriceUsd ?? 0)}</span>
                        </div>
                      }
                      {
                        reportSumAllOrder?.discount > 0 && 
                        <div className="d-flex align-items-center">
                          <span className="stat-title mb-1"> ສ່ວນຫລຸດ:{" "}{numberFormat(reportSumAllOrder?.discount ?? 0)} ₭</span>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          
          {/* Shipping Cost Card */}
          <Col lg={4} md={6} sm={12} className="mb-3">
            <Card className="stat-card shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="stat-icon shipping-icon me-3">
                    <LocalActivityIcon style={{ fontSize: 24 }} />
                  </div>
                  <div className="stat-info">
                    <h6 className="stat-title mb-1">ຄ່າຂົນສົ່ງທັງຫມົດ</h6>
                    <h3 className="stat-value mb-0">{numberFormat(reportSumAllOrder?.shipCost ?? 0)} ₭</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Chart Section */}
        <div style={{ width: "100%", marginBottom: "10px" }}>
          <Card className="chart-container">
            <Card.Body className={reportOptionsLoading ? 'loading-overlay' : ''}>
              {reportOptionsLoading && (
                <div className="loading-spinner">
                  <Spinner animation="border" variant="primary" />
                </div>
              )}
              {reportOptionsError && (
                <div className="error-message">
                  <strong>ເກີດຂໍ້ຜິດພາດ:</strong> {reportOptionsError.message}
                </div>
              )}
              <h5 className="chart-title">ສະຖິຕິ</h5>
              <ReactApexChart
                options={state.options}
                series={state.series}
                type="bar"
                height={400}
              />
            </Card.Body>
          </Card>
        </div>

        {/* Best Sellers Table */}
        <div style={{ width: "100%", marginBottom: "20px" }}>
          <Card className="shadow-sm">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">ລາຍງານການຂາຍສິນຄ້າ</h5>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <span className="report-title">
                  <LocalActivityIcon
                    sx={{
                      color: "#3C169B",
                      marginRight: "8px",
                      verticalAlign: "middle"
                    }}
                  />
                  ສິນຄ້າຂາຍດີ {ConvertDay(activeButton)}
                </span>
              </div>
              {reportOptionsLoading ? (
                <AnimationLoading />
              ) : (
                <Table striped hover responsive>
                  <thead className="table-header">
                    <tr>
                      <th>ລຳດັບ</th>
                      <th>ຊື່ສິນຄ້າ</th>
                      <th>ປະເພດ</th>
                      <th>ແບຣນ</th>
                      <th>ຫນ່ວຍ</th>
                      <th>ຈຳນວນ</th>
                      <th>ລາຄາ</th>
                      <th>ຄ່າຂົນສົ່ງ</th>
                      <th>ສະຖານະ</th>
                    </tr>
                  </thead>

                  {reportBestSale.length > 0 ? (
                    <tbody>
                      {reportBestSale?.map((data, index) => {
                        // Function to get status color
                        const getStatusColor = (status) => {
                          switch(status?.toLowerCase()) {
                            case 'completed':
                            case 'success':
                            case 'delivered':
                              return '#28a745'; // Green
                            case 'pending':
                            case 'processing':
                              return '#ffc107'; // Yellow
                            case 'cancelled':
                            case 'failed':
                            case 'rejected':
                              return '#dc3545'; // Red
                            case 'shipped':
                            case 'in_transit':
                              return '#17a2b8'; // Blue
                            default:
                              return '#6c757d'; // Gray
                          }
                        };
                        
                        return (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{data?.productName}</td>
                            <td>{data?.category}</td>
                            <td>{data?.brand}</td>
                            <td>{data?.unit}</td>
                            <td>{data?.amount}</td>
                            <td>{numberFormat(data?.price ?? 0)} {convertCurrency(data?.currency)}</td>
                            <td>{numberFormat(data?.shippingCost ?? 0)} ₭</td>
                            <td>
                              <span 
                                style={{
                                  backgroundColor: getStatusColor(data?.status),
                                  color: 'white',
                                  padding: '4px 8px',
                                  borderRadius: '4px',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  textTransform: 'capitalize'
                                }}
                              >
                                {data?.status === 'COMPLETED' ? "ຊຳລະແລ້ວ" : (data?.status === "INDEBTED" ? "ຕີດຫນີ້" : 'unknown')}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  ) : (
                    <tbody>
                      <tr>
                        <td colSpan={9}>
                          <div className="empty-state">
                            <EmptyComponent />
                            <p>ຍັງບໍ່ມີລາຍການສິນຄ້າ!!!</p>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  )}
                </Table>
              )}
              
              {/* Pagination Controls */}
              {reportBestSale.length > 0 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="d-flex align-items-center">
                    <span className="me-2">ສະແດງ:</span>
                    <Form.Select 
                      size="sm" 
                      style={{ width: 'auto' }}
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(parseInt(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </Form.Select>
                    <span className="ms-2">ລາຍການຕໍ່ໜ້າ</span>
                  </div>
                  
                  <div className="d-flex align-items-center">
                    <span className="me-3">
                      ສະແດງ {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, totalItems)} ຈາກ {totalItems} ລາຍການ
                    </span>
                    
                    <div className="d-flex">
                      <Button 
                        variant="outline-secondary" 
                        size="sm" 
                        className="me-1"
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage(currentPage - 1)}
                      >
                        ກ່ອນໜ້າ
                      </Button>
                      
                      {/* Page Numbers */}
                      {Array.from({ length: Math.ceil(totalItems / itemsPerPage) }, (_, i) => i + 1)
                        .filter(page => {
                          const totalPages = Math.ceil(totalItems / itemsPerPage);
                          if (totalPages <= 5) return true;
                          if (page === 1 || page === totalPages) return true;
                          if (page >= currentPage - 1 && page <= currentPage + 1) return true;
                          return false;
                        })
                        .map((page, index, array) => {
                          const prevPage = array[index - 1];
                          const showEllipsis = prevPage && page - prevPage > 1;
                          
                          return (
                            <div key={page} className="d-flex">
                              {showEllipsis && (
                                <Button variant="outline-secondary" size="sm" className="me-1" disabled>
                                  ...
                                </Button>
                              )}
                              <Button 
                                variant={currentPage === page ? "primary" : "outline-secondary"} 
                                size="sm" 
                                className="me-1"
                                onClick={() => setCurrentPage(page)}
                              >
                                {page}
                              </Button>
                            </div>
                          );
                        })
                      }
                      
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        disabled={currentPage === Math.ceil(totalItems / itemsPerPage)}
                        onClick={() => setCurrentPage(currentPage + 1)}
                      >
                        ຕໍ່ໄປ
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default ReportOptions;
